import { useState } from 'react';

interface TypeOption {
  value: string;
  label: string;
  imageUrl?: string;
}

interface ShirtSelectionSettingsData {
  showTypeSelection?: boolean;
  typeOptions?: TypeOption[];
  showSizeChart?: boolean;
  sizeChartImageUrl?: string;
  sizeChartCaption?: string;
  adultSizes?: string[];
  kidsSizes?: string[];
  kidsLabel?: string;
}

interface Props {
  settings: ShirtSelectionSettingsData;
  onChange: (settings: ShirtSelectionSettingsData) => void;
}

const DEFAULT_ADULT_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];
const DEFAULT_KIDS_SIZES = ['120', '130', '140', '150'];

export default function ShirtSelectionSettings({ settings, onChange }: Props) {
  const [activeSection, setActiveSection] = useState<'types' | 'sizes' | 'chart'>('types');

  const typeOptions = settings.typeOptions || [];
  const adultSizes = settings.adultSizes || [];
  const kidsSizes = settings.kidsSizes || [];

  function addTypeOption() {
    const newOption: TypeOption = {
      value: `type_${Date.now()}`,
      label: '新款式',
    };
    onChange({ ...settings, typeOptions: [...typeOptions, newOption] });
  }

  function updateTypeOption(value: string, updates: Partial<TypeOption>) {
    onChange({
      ...settings,
      typeOptions: typeOptions.map(t => t.value === value ? { ...t, ...updates } : t),
    });
  }

  function removeTypeOption(value: string) {
    onChange({
      ...settings,
      typeOptions: typeOptions.filter(t => t.value !== value),
    });
  }

  function toggleAdultSize(size: string) {
    if (adultSizes.includes(size)) {
      onChange({ ...settings, adultSizes: adultSizes.filter(s => s !== size) });
    } else {
      onChange({ ...settings, adultSizes: [...adultSizes, size] });
    }
  }

  function toggleKidsSize(size: string) {
    if (kidsSizes.includes(size)) {
      onChange({ ...settings, kidsSizes: kidsSizes.filter(s => s !== size) });
    } else {
      onChange({ ...settings, kidsSizes: [...kidsSizes, size] });
    }
  }

  return (
    <div className="space-y-4">
      {/* Section Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
        <button
          onClick={() => setActiveSection('types')}
          className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            activeSection === 'types' ? 'bg-white shadow text-primary' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          款式 ({typeOptions.length})
        </button>
        <button
          onClick={() => setActiveSection('sizes')}
          className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            activeSection === 'sizes' ? 'bg-white shadow text-primary' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          尺寸
        </button>
        <button
          onClick={() => setActiveSection('chart')}
          className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            activeSection === 'chart' ? 'bg-white shadow text-primary' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          尺寸表
        </button>
      </div>

      {/* Types Section */}
      {activeSection === 'types' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <span className="text-sm font-medium">顯示款式選擇</span>
              <p className="text-xs text-gray-500 mt-0.5">允許用戶選擇不同款式</p>
            </div>
            <button
              onClick={() => onChange({ ...settings, showTypeSelection: !settings.showTypeSelection })}
              className={`w-10 h-5 rounded-full transition-colors relative ${
                settings.showTypeSelection ? 'bg-primary' : 'bg-gray-300'
              }`}
            >
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                settings.showTypeSelection ? 'left-5' : 'left-0.5'
              }`} />
            </button>
          </div>

          {settings.showTypeSelection && (
            <>
              {typeOptions.map((opt, idx) => (
                <div key={opt.value} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-500">款式 {idx + 1}</span>
                    <button
                      onClick={() => removeTypeOption(opt.value)}
                      className="text-red-500 hover:text-red-600 text-xs"
                    >
                      刪除
                    </button>
                  </div>
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={opt.label}
                      onChange={(e) => updateTypeOption(opt.value, { label: e.target.value })}
                      placeholder="款式名稱"
                      className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-primary"
                    />
                    <input
                      type="url"
                      value={opt.imageUrl || ''}
                      onChange={(e) => updateTypeOption(opt.value, { imageUrl: e.target.value })}
                      placeholder="圖片網址 (建議 400x300)"
                      className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>
              ))}
              <button
                onClick={addTypeOption}
                className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-primary hover:text-primary transition-colors"
              >
                + 新增款式
              </button>
            </>
          )}
        </div>
      )}

      {/* Sizes Section */}
      {activeSection === 'sizes' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">成人尺寸</label>
            <div className="flex flex-wrap gap-2">
              {DEFAULT_ADULT_SIZES.map((size) => (
                <button
                  key={size}
                  onClick={() => toggleAdultSize(size)}
                  className={`px-3 py-1.5 text-xs border rounded-lg transition-colors ${
                    adultSizes.includes(size)
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">已選: {adultSizes.length > 0 ? adultSizes.join(', ') : '無'}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">兒童尺寸</label>
            <div className="flex flex-wrap gap-2">
              {DEFAULT_KIDS_SIZES.map((size) => (
                <button
                  key={size}
                  onClick={() => toggleKidsSize(size)}
                  className={`px-3 py-1.5 text-xs border rounded-lg transition-colors ${
                    kidsSizes.includes(size)
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">已選: {kidsSizes.length > 0 ? kidsSizes.join(', ') : '無'}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">兒童區標籤</label>
            <input
              type="text"
              value={settings.kidsLabel || ''}
              onChange={(e) => onChange({ ...settings, kidsLabel: e.target.value })}
              placeholder="例: 兒童尺寸"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary"
            />
          </div>
        </div>
      )}

      {/* Size Chart Section */}
      {activeSection === 'chart' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <span className="text-sm font-medium">顯示尺寸表</span>
              <p className="text-xs text-gray-500 mt-0.5">顯示尺寸對照圖片</p>
            </div>
            <button
              onClick={() => onChange({ ...settings, showSizeChart: !settings.showSizeChart })}
              className={`w-10 h-5 rounded-full transition-colors relative ${
                settings.showSizeChart ? 'bg-primary' : 'bg-gray-300'
              }`}
            >
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                settings.showSizeChart ? 'left-5' : 'left-0.5'
              }`} />
            </button>
          </div>

          {settings.showSizeChart && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">尺寸表圖片 (建議 800x600)</label>
                <input
                  type="url"
                  value={settings.sizeChartImageUrl || ''}
                  onChange={(e) => onChange({ ...settings, sizeChartImageUrl: e.target.value })}
                  placeholder="https://example.com/size-chart.jpg"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">圖片說明</label>
                <input
                  type="text"
                  value={settings.sizeChartCaption || ''}
                  onChange={(e) => onChange({ ...settings, sizeChartCaption: e.target.value })}
                  placeholder="例: 點擊查看大圖"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary"
                />
              </div>

              {settings.sizeChartImageUrl && (
                <div className="p-2 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-2">預覽</p>
                  <img
                    src={settings.sizeChartImageUrl}
                    alt="Size Chart Preview"
                    className="max-w-full h-auto rounded border border-gray-200"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
